import { useState, useCallback, useMemo } from 'react';
import type { AdminGreeting, GreetingStatus } from '@/mocks/adminGreetings';
import { adminGreetings, statusLabels, statusColors } from '@/mocks/adminGreetings';
import { SwalAlert } from '@/hooks/useSweetAlert';

type FilterTab = 'todos' | GreetingStatus;

const filterTabs: { id: FilterTab; label: string; icon: string }[] = [
  { id: 'todos', label: 'Todos', icon: 'ri-inbox-line' },
  { id: 'nuevo', label: 'Nuevos', icon: 'ri-mail-unread-line' },
  { id: 'leido', label: 'Leídos', icon: 'ri-check-double-line' },
  { id: 'al_aire', label: 'Al Aire', icon: 'ri-broadcast-line' },
  { id: 'archivado', label: 'Archivados', icon: 'ri-archive-line' },
];

function formatTime(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'Ahora';
  if (diffMin < 60) return `Hace ${diffMin}m`;
  const diffHrs = Math.floor(diffMin / 60);
  if (diffHrs < 24) return `Hace ${diffHrs}h`;
  const diffDays = Math.floor(diffHrs / 24);
  if (diffDays === 1) return 'Ayer';
  return d.toLocaleDateString('es-CR', { day: 'numeric', month: 'short' });
}

function formatFullTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString('es-CR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

export default function AdminGreetingsPage() {
  const [greetings, setGreetings] = useState<AdminGreeting[]>(adminGreetings);
  const [activeFilter, setActiveFilter] = useState<FilterTab>('todos');
  const [searchQuery, setSearchQuery] = useState('');
  const [replyTarget, setReplyTarget] = useState<AdminGreeting | null>(null);
  const [replyText, setReplyText] = useState('');

  /* ─── Counts ─── */
  const counts = useMemo(() => {
    const c: Record<string, number> = { nuevo: 0, leido: 0, al_aire: 0, archivado: 0 };
    greetings.forEach((g) => { c[g.status] = (c[g.status] || 0) + 1; });
    return c;
  }, [greetings]);

  /* ─── Filtered list ─── */
  const filteredGreetings = useMemo(() => {
    let list = greetings;
    if (activeFilter !== 'todos') {
      list = list.filter((g) => g.status === activeFilter);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        (g) =>
          g.senderName.toLowerCase().includes(q) ||
          g.content.toLowerCase().includes(q) ||
          g.location.toLowerCase().includes(q),
      );
    }
    return list;
  }, [greetings, activeFilter, searchQuery]);

  /* ─── Actions ─── */
  const handleMarkRead = useCallback((item: AdminGreeting) => {
    setGreetings((prev) =>
      prev.map((g) => (g.id === item.id ? { ...g, status: 'leido' as GreetingStatus, readAt: new Date().toISOString() } : g)),
    );
    SwalAlert.toast('Saludo marcado como leído', 'success');
  }, []);

  const handleReadOnAir = useCallback((item: AdminGreeting) => {
    setGreetings((prev) =>
      prev.map((g) =>
        g.id === item.id
          ? { ...g, status: 'al_aire' as GreetingStatus, readAt: g.readAt || new Date().toISOString(), airedAt: new Date().toISOString() }
          : g,
      ),
    );
    SwalAlert.toast(`Saludo de ${item.senderName} al aire`, 'success');
  }, []);

  const handleArchive = useCallback(async (item: AdminGreeting) => {
    const result = await SwalAlert.confirm(
      '¿Archivar saludo?',
      `El saludo de "${item.senderName}" se moverá al archivo.`,
    );
    if (!result.isConfirmed) return;
    setGreetings((prev) =>
      prev.map((g) => (g.id === item.id ? { ...g, status: 'archivado' as GreetingStatus, archivedAt: new Date().toISOString() } : g)),
    );
    SwalAlert.toast('Saludo archivado', 'info');
  }, []);

  const handleToggleHighlight = useCallback((item: AdminGreeting) => {
    setGreetings((prev) =>
      prev.map((g) => (g.id === item.id ? { ...g, isHighlighted: !g.isHighlighted } : g)),
    );
    SwalAlert.toast(
      item.isHighlighted ? 'Saludo removido de destacados' : 'Saludo destacado',
      'success',
    );
  }, []);

  const handleOpenReply = useCallback((item: AdminGreeting) => {
    setReplyTarget(item);
    setReplyText('');
  }, []);

  const handleSendReply = useCallback(() => {
    if (!replyTarget || !replyText.trim()) return;
    SwalAlert.toast(`Respuesta enviada a ${replyTarget.senderName}`, 'success');
    setGreetings((prev) =>
      prev.map((g) =>
        g.id === replyTarget.id ? { ...g, status: g.status === 'nuevo' ? 'leido' as GreetingStatus : g.status, readAt: g.readAt || new Date().toISOString() } : g,
      ),
    );
    setReplyTarget(null);
    setReplyText('');
  }, [replyTarget, replyText]);

  /* ─── Summary stats ─── */
  const totalGreetings = greetings.length;
  const newCount = counts.nuevo || 0;

  return (
    <div className="min-h-screen bg-background-50">
      <div className="max-w-6xl mx-auto px-4 md:px-6 py-6 md:py-8">
        {/* ─── Header ─── */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-500 flex items-center justify-center">
                <i className="ri-user-heart-line text-background-50 text-lg"></i>
              </div>
              <div>
                <h1 className="text-xl md:text-2xl font-bold text-foreground-950">Saludos en Tiempo Real</h1>
                <p className="text-xs text-foreground-400 mt-0.5">Gestiona los saludos que envían tus oyentes</p>
              </div>
            </div>
          </div>
          {/* Quick stats */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-background-100">
              <span className="text-2xl font-bold text-foreground-950">{totalGreetings}</span>
              <span className="text-xs text-foreground-400">Total</span>
            </div>
            {newCount > 0 && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-primary-50">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-primary-500"></span>
                </span>
                <span className="text-2xl font-bold text-primary-600">{newCount}</span>
                <span className="text-xs text-primary-500">Pendientes</span>
              </div>
            )}
          </div>
        </div>

        {/* ─── Status bar ─── */}
        <div className="flex flex-wrap items-center gap-2 mb-5">
          {filterTabs.map((tab) => {
            const isActive = activeFilter === tab.id;
            const tabCount = tab.id === 'todos' ? totalGreetings : (counts[tab.id] || 0);
            return (
              <button
                key={tab.id}
                onClick={() => setActiveFilter(tab.id)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold whitespace-nowrap cursor-pointer transition-all ${
                  isActive
                    ? 'bg-emerald-500 text-background-50 shadow-sm'
                    : 'bg-background-100 text-foreground-500 hover:bg-background-200'
                }`}
              >
                <i className={`${tab.icon} text-[11px]`}></i>
                {tab.label}
                <span className={`px-1.5 py-0 rounded-full text-[10px] font-bold ${
                  isActive ? 'bg-background-50/25 text-background-50' : 'bg-background-200 text-foreground-400'
                }`}>{tabCount}</span>
              </button>
            );
          })}
          {/* Search */}
          <div className="ml-auto">
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-xs"></i>
              <input
                type="text"
                placeholder="Buscar por nombre o mensaje..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-56 pl-8 pr-3 py-2 rounded-full bg-background-100 border border-background-200 text-xs text-foreground-800 placeholder:text-foreground-400 outline-none focus:border-emerald-300 transition-colors"
              />
            </div>
          </div>
        </div>

        {/* ─── Greeting Cards ─── */}
        <div className="space-y-3">
          {filteredGreetings.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="w-16 h-16 rounded-2xl bg-background-100 flex items-center justify-center mb-3">
                <i className="ri-user-heart-line text-foreground-300 text-2xl"></i>
              </div>
              <p className="text-sm text-foreground-500 font-medium mb-1">No hay saludos</p>
              <p className="text-xs text-foreground-400">
                {activeFilter === 'todos'
                  ? 'Aún no has recibido saludos de tus oyentes'
                  : `No tienes saludos en estado "${statusLabels[activeFilter as GreetingStatus]}"`}
              </p>
            </div>
          ) : (
            filteredGreetings.map((greeting) => {
              const sc = statusColors[greeting.status];
              const isArchived = greeting.status === 'archivado';
              return (
                <div
                  key={greeting.id}
                  className={`rounded-xl border p-4 transition-all ${
                    greeting.isHighlighted
                      ? 'ring-1 ring-amber-300 bg-amber-50/40 border-amber-200'
                      : isArchived
                        ? 'bg-background-50 border-background-200 opacity-75'
                        : greeting.status === 'nuevo'
                          ? 'bg-emerald-50/40 border-emerald-200'
                          : 'bg-background-50 border-background-200 hover:border-background-300'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    {/* Avatar */}
                    <div className="w-10 h-10 rounded-full bg-background-200 overflow-hidden shrink-0">
                      <img
                        src={greeting.senderAvatar}
                        alt={greeting.senderName}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center flex-wrap gap-2 mb-1">
                        <span className="text-sm font-semibold text-foreground-800">{greeting.senderName}</span>
                        <span className="text-[11px] text-foreground-400 flex items-center gap-1">
                          <i className="ri-map-pin-line text-[10px]"></i>
                          {greeting.location}
                        </span>
                        {/* Status badge */}
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${sc.bg} ${sc.text}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${sc.dot}`}></span>
                          {statusLabels[greeting.status]}
                        </span>
                        {/* Highlighted star */}
                        {greeting.isHighlighted && (
                          <span className="inline-flex items-center gap-0.5 text-amber-500 text-[10px] font-semibold">
                            <i className="ri-star-fill text-[10px]"></i>
                            Destacado
                          </span>
                        )}
                        <span className="text-[11px] text-foreground-400 ml-auto" title={formatFullTime(greeting.timestamp)}>
                          {formatTime(greeting.timestamp)}
                        </span>
                      </div>

                      {/* Message */}
                      <p className="text-sm text-foreground-700 leading-relaxed mb-3">{greeting.content}</p>

                      {/* Metadata line */}
                      <div className="flex items-center flex-wrap gap-3 mb-3">
                        {greeting.readAt && (
                          <span className="text-[10px] text-foreground-400 flex items-center gap-1">
                            <i className="ri-check-double-line text-[10px]"></i>
                            Leído: {formatTime(greeting.readAt)}
                          </span>
                        )}
                        {greeting.airedAt && (
                          <span className="text-[10px] text-rose-500 flex items-center gap-1 font-medium">
                            <i className="ri-broadcast-line text-[10px]"></i>
                            Al aire: {formatTime(greeting.airedAt)}
                          </span>
                        )}
                        {greeting.archivedAt && (
                          <span className="text-[10px] text-foreground-400 flex items-center gap-1">
                            <i className="ri-archive-line text-[10px]"></i>
                            Archivado: {formatTime(greeting.archivedAt)}
                          </span>
                        )}
                      </div>

                      {/* Actions */}
                      {!isArchived && (
                        <div className="flex items-center flex-wrap gap-1.5">
                          {/* Responder — always available */}
                          <button
                            onClick={() => handleOpenReply(greeting)}
                            className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-primary-500 text-background-50 text-[11px] font-semibold hover:bg-primary-600 cursor-pointer transition-colors whitespace-nowrap"
                          >
                            <i className="ri-reply-line text-[11px]"></i>
                            Responder
                          </button>

                          {/* Leer al Aire — for nuevo and leido */}
                          {greeting.status !== 'al_aire' && (
                            <button
                              onClick={() => handleReadOnAir(greeting)}
                              className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-rose-500 text-background-50 text-[11px] font-semibold hover:bg-rose-600 cursor-pointer transition-colors whitespace-nowrap"
                            >
                              <i className="ri-broadcast-line text-[11px]"></i>
                              Leer al Aire
                            </button>
                          )}

                          {/* Marcar leído — only for nuevo */}
                          {greeting.status === 'nuevo' && (
                            <button
                              onClick={() => handleMarkRead(greeting)}
                              className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-background-100 text-foreground-500 text-[11px] font-semibold hover:bg-background-200 cursor-pointer transition-colors whitespace-nowrap"
                            >
                              <i className="ri-check-double-line text-[11px]"></i>
                              Marcar Leído
                            </button>
                          )}

                          {/* Destacar — always available when not archived */}
                          <button
                            onClick={() => handleToggleHighlight(greeting)}
                            className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-[11px] font-semibold cursor-pointer transition-colors whitespace-nowrap ${
                              greeting.isHighlighted
                                ? 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                                : 'bg-background-100 text-foreground-500 hover:bg-background-200'
                            }`}
                          >
                            <i className={`${greeting.isHighlighted ? 'ri-star-fill' : 'ri-star-line'} text-[11px]`}></i>
                            {greeting.isHighlighted ? 'Destacado' : 'Destacar'}
                          </button>

                          {/* Archivar — always available */}
                          <button
                            onClick={() => handleArchive(greeting)}
                            className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-background-100 text-foreground-500 text-[11px] font-semibold hover:bg-background-200 cursor-pointer transition-colors whitespace-nowrap"
                          >
                            <i className="ri-archive-line text-[11px]"></i>
                            Archivar
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* ─── Reply Modal ─── */}
      {replyTarget && (
        <>
          <div
            className="fixed inset-0 z-[80] bg-black/40 transition-opacity"
            onClick={() => setReplyTarget(null)}
          ></div>
          <div className="fixed inset-0 z-[90] flex items-center justify-center p-4">
            <div className="bg-background-50 rounded-2xl shadow-lg border border-background-200 w-full max-w-lg max-h-[90vh] flex flex-col">
              {/* Modal header */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-background-200 shrink-0">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-background-200 overflow-hidden shrink-0">
                    <img
                      src={replyTarget.senderAvatar}
                      alt={replyTarget.senderName}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-foreground-950">Responder a {replyTarget.senderName}</h3>
                    <p className="text-[10px] text-foreground-400">{replyTarget.location}</p>
                  </div>
                </div>
                <button
                  onClick={() => setReplyTarget(null)}
                  className="w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors"
                >
                  <i className="ri-close-line text-foreground-500"></i>
                </button>
              </div>

              {/* Original message */}
              <div className="px-5 py-3 bg-background-100/50 border-b border-background-200">
                <p className="text-[11px] text-foreground-400 mb-1">Saludo original:</p>
                <p className="text-xs text-foreground-700 leading-relaxed italic">"{replyTarget.content}"</p>
              </div>

              {/* Reply area */}
              <div className="flex-1 p-5 overflow-y-auto">
                <label className="text-xs font-semibold text-foreground-700 block mb-2">Tu respuesta:</label>
                <textarea
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder="Escribe tu respuesta al oyente..."
                  maxLength={500}
                  rows={5}
                  className="w-full px-4 py-3 rounded-xl bg-background-50 border border-background-200 text-sm text-foreground-800 placeholder:text-foreground-400 outline-none resize-none focus:border-emerald-300 transition-colors"
                />
                <p className="text-[10px] text-foreground-400 mt-1.5 text-right">{replyText.length}/500</p>
              </div>

              {/* Modal footer */}
              <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-background-200 shrink-0">
                <button
                  onClick={() => setReplyTarget(null)}
                  className="px-4 py-2 rounded-lg bg-background-100 text-foreground-500 text-xs font-semibold hover:bg-background-200 cursor-pointer transition-colors whitespace-nowrap"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSendReply}
                  disabled={!replyText.trim()}
                  className={`px-5 py-2 rounded-lg text-xs font-semibold cursor-pointer transition-colors whitespace-nowrap ${
                    replyText.trim()
                      ? 'bg-emerald-500 text-background-50 hover:bg-emerald-600'
                      : 'bg-background-200 text-foreground-400 cursor-not-allowed'
                  }`}
                >
                  <i className="ri-send-plane-line text-[11px] mr-1.5"></i>
                  Enviar Respuesta
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}